import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Canonical from '../../seo/Canonical';
import LogisticsPageHero from '../Logistics/LogisticsPageHero';
import Footer from '../Layouts/Footer';
import { fetchPublicJobs, type Job } from '../../admin/api';
import JobApplyModal from './JobApplyModal';
import './Careers.css';

const Careers: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [applyingJob, setApplyingJob] = useState<Job | null>(null);

  useEffect(() => {
    fetchPublicJobs().then(data => {
      setJobs(data);
    }).catch(err => {
      console.error('Failed to load jobs', err);
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  const toggleExpand = (id: string) => {
    setExpandedJobId(prev => (prev === id ? null : id));
  };

  return (
    <div className="careers-page logistics-page">
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />
      <Helmet>
        <title>Careers at Pick Pack Pro | UK Ecommerce Fulfilment Jobs</title>
        <meta name="description" content="Explore career opportunities at Pick Pack Pro. Join our dynamic fulfilment team in Milton Keynes and help us deliver exceptional ecommerce logistics." />
      </Helmet>

      <LogisticsPageHero
        ariaLabel="Ecommerce Fulfilment Careers at Pick Pack Pro"
        imageSrc="/Images/careers-team-new.jpg"
        imageAlt="Careers at Pick Pack Pro warehouse"
        titleLight="Ecommerce Fulfilment "
        titleAccent="Careers"
        description={
          <>
            <p>
              At Pick Pack Pro, we are always on the lookout for talented, hard-working, and passionate individuals to join our growing fulfilment team. Operating from our state-of-the-art facility in Milton Keynes, we provide a dynamic work environment where every team member plays a crucial role in our success. 
            </p>
            <p>
              Whether you are experienced in warehouse logistics or looking to start your career in ecommerce fulfilment, we'd love to hear from you.
            </p>
          </>
        }
        primaryCta={{ label: 'Contact Us', to: '/contact-fulfillment-services' }}
        secondaryCta={{ label: 'Email Us', to: 'mailto:info@pickpackpro.co.uk' }}
      />
      
      <section className="careers-seo-content" style={{ maxWidth: '1000px', margin: '40px auto 20px', padding: '0 20px', color: '#334155', lineHeight: '1.7', fontFamily: 'var(--font-body)' }}>
        <div style={{ background: '#f8fafc', padding: '24px 32px', borderRadius: '12px', borderLeft: '4px solid #FF7900', marginBottom: '40px' }}>
          <h2 style={{ fontSize: '1.25rem', color: '#0f172a', marginBottom: '12px', marginTop: 0 }}>Executive Summary: Joining Pick Pack Pro</h2>
          <p style={{ margin: 0 }}>
            <strong>Bottom line:</strong> Pick Pack Pro is rapidly expanding its Milton Keynes warehouse operations. We offer competitive salaries, structured training, and excellent career progression for logistics professionals, warehouse operatives, and ecommerce specialists looking to thrive in a fast-paced UK fulfilment environment.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px', marginBottom: '40px' }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', color: '#1e293b', marginBottom: '16px' }}>What is a 3PL career?</h2>
            <p style={{ marginBottom: '16px' }}>
              A Third-Party Logistics (3PL) career involves managing the storage, picking, packing, and dispatch of products on behalf of eCommerce brands, ensuring efficient end-to-end supply chain operations. It is a highly dynamic field driven by modern technology and operational precision.
            </p>
            <h2 style={{ fontSize: '1.5rem', color: '#1e293b', marginBottom: '16px' }}>Who should apply for these roles?</h2>
            <p>
              Our roles are tailored for a diverse audience: warehouse operatives seeking stable, engaging shifts, eCommerce managers looking for strategic logistics positions, and anyone passionate about supply chain excellence. Whether you are an entry-level packer or an experienced shift supervisor, you will find a supportive environment here.
            </p>
          </div>
          
          <div>
            <h2 style={{ fontSize: '1.5rem', color: '#1e293b', marginBottom: '16px' }}>Why build a career in ecommerce fulfilment with us?</h2>
            <p style={{ marginBottom: '16px' }}>
              We provide a supportive, modern warehouse environment strictly aligned with and often exceeding the <a href="https://www.gov.uk/national-minimum-wage-rates" target="_blank" rel="noopener noreferrer" style={{ color: '#1a44e5', textDecoration: 'underline' }}>UK National Living Wage</a> guidelines. <Link to="/about-our-fulfillment-company" style={{ color: '#1a44e5', textDecoration: 'underline' }}>About our company</Link>, we prioritize employee safety and growth above all else.
            </p>
            <p>
              <strong>Our Track Record:</strong> Based on our internal HR metrics from 2024, our Milton Keynes team members consistently report high job satisfaction due to our structured onboarding, safety-first policies, and clear progression paths in the growing eCommerce logistics sector. <Link to="/contact-fulfillment-services" style={{ color: '#1a44e5', textDecoration: 'underline' }}>Contact our team</Link> to learn more.
            </p>
          </div>
        </div>

        <h3 style={{ fontSize: '1.25rem', color: '#1e293b', marginBottom: '12px' }}>How to apply for an open position?</h3>
        <p style={{ marginBottom: '20px' }}>
          To apply for an open position, simply review the active roles listed below, click the "View Details" button to read the full job description, and hit "Apply Now" to submit your CV and cover letter directly to our hiring team.
        </p>
      </section>

      <section className="open-positions-section">
        <div className="open-positions-container">
          <h2 className="section-title">Open Positions</h2>
          
          {loading ? (
            <p className="loading-text">Loading jobs...</p>
          ) : jobs.length === 0 ? (
            <div className="no-jobs">
              <h3>No open positions at the moment.</h3>
              <p>Please check back later or send us your CV proactively via email.</p>
            </div>
          ) : (
            <div className="jobs-list">
              {jobs.map(job => (
                <div key={job._id} className={`job-card ${expandedJobId === job._id ? 'expanded' : ''}`}>
                  <div className="job-card-header" onClick={() => toggleExpand(job._id)}>
                    <div className="job-card-info">
                      <h3 className="job-title">{job.title}</h3>
                      <p className="job-tagline">{job.tagline}</p>
                      <div className="job-tags">
                        <span className="job-tag">{job.department}</span>
                        <span className="job-tag">{job.employmentType}</span>
                        <span className="job-tag">{job.workplaceType}</span>
                        <span className="job-tag">{job.city}</span>
                      </div>
                    </div>
                    <div className="job-card-action">
                      <button className="expand-btn">
                        {expandedJobId === job._id ? 'View Less' : 'View Details'}
                      </button>
                    </div>
                  </div>
                  
                  {expandedJobId === job._id && (
                    <div className="job-card-body">
                      <div className="job-details-grid">
                        <div className="detail-item"><strong>Experience:</strong> {job.experienceLevel}</div>
                        <div className="detail-item"><strong>Notice Period:</strong> {job.noticePeriod}</div>
                        <div className="detail-item"><strong>Education:</strong> {job.minEducation}</div>
                        {(job.salaryMin || job.salaryMax) && (
                          <div className="detail-item">
                            <strong>Salary:</strong> {job.currency} {job.salaryMin} - {job.salaryMax}
                          </div>
                        )}
                      </div>
                      
                      <div className="job-long-text">
                        <h4>Responsibilities:</h4>
                        <p>{job.responsibilities}</p>
                        
                        <h4>Requirements:</h4>
                        <p>{job.requirements}</p>
                        
                        {job.preferred && (
                          <>
                            <h4>Preferred (Nice-to-have):</h4>
                            <p>{job.preferred}</p>
                          </>
                        )}
                        
                        {job.benefits && (
                          <>
                            <h4>Perks & Benefits:</h4>
                            <p>{job.benefits}</p>
                          </>
                        )}
                      </div>
                      
                      <div className="job-card-ctas">
                        <button onClick={() => setApplyingJob(job)} className="btn-apply-primary" style={{ border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '1rem' }}>Apply Now</button>
                        <a href={`mailto:info@pickpackpro.co.uk?subject=Application for ${encodeURIComponent(job.title)}`} className="btn-apply-secondary">Email Us</a>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      {applyingJob && <JobApplyModal job={applyingJob} onClose={() => setApplyingJob(null)} />}
    </div>
  );
};

export default Careers;
